<template>
    <div
        :class="[
            'vue-star-rating d-flex align-items-center',
            {
                'vue-star-rating-rtl': rtl
            },
            {
                'vue-star-rating-inline': inline
            }
        ]"
    >
        <div
            class="sr-only"
        >
            <slot
                name="screen-reader"
                :rating="selectedRating"
                :stars="maxRating"
            >
                <span>Rated {{ selectedRating }} stars out of {{ maxRating }}</span>
            </slot>
        </div>

        <div
            class="vue-star-rating d-flex align-items-center"
            :style="{
                'gap': gap + 'px'
            }"
            @mouseleave="resetRating"
        >
            <span
                v-for="n in maxRating"
                class="d-flex flex-column align-center justify-center"
                :key="n"
                :class="[{ 'vue-star-rating-pointer': !readonly }, 'vue-star-rating-star']"
            >
                <client-only>
                    <StarItem
                        :fill="fillLevel[n - 1]"
                        :size="starSize"
                        :points="starPoints"
                        :star-id="n"
                        :active-color="currentActiveColor"
                        :inactive-color="inactiveColor"
                        :border-color="borderColor"
                        :active-border-color="currentActiveBorderColor"
                        :border-width="borderWidth"
                        :rounded-corners="roundedCorners"
                        :rtl="rtl"
                        :glow="glow"
                        :glow-color="glowColor"
                        :animate="animate"
                        @star-selected="setRating($event, true)"
                        @star-mouse-move="setRating"
                    ></StarItem>
                </client-only>
            </span>
            <span v-if="showRating" :class="['vue-star-rating-rating-text', textClass]">
                {{ formattedRating }}
            </span>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import StarItem from '#x1-base/components/star-rating/StarItem/StarItem.vue';

    const props = withDefaults(
        defineProps<{
            increment?: number;
            rating?: number;
            roundStartRating?: boolean;
            activeColor?: string | string[];
            inactiveColor?: string;
            maxRating?: number;
            starPoints?: number[];
            starSize?: number;
            showRating?: boolean;
            readonly?: boolean;
            textClass?: string;
            inline?: boolean;
            borderColor?: string;
            activeBorderColor?: string | string[] | null;
            borderWidth?: number;
            roundedCorners?: boolean;
            gap?: number;
            // padding?: number;
            rtl?: boolean;
            fixedPoints?: number | null;
            glow?: number;
            glowColor?: string;
            clearable?: boolean;
            activeOnClick?: boolean;
            animate?: boolean;
        }>(),
        {
            increment: 1,
            rating: 0,
            roundStartRating: true,
            activeColor: '#ffd055',
            inactiveColor: '#d8d8d8',
            maxRating: 5,
            starPoints: () => [],
            starSize: 50,
            showRating: true,
            readonly: false,
            textClass: '',
            inline: false,
            borderColor: '#999',
            activeBorderColor: null,
            borderWidth: 0,
            roundedCorners: false,
            gap: 0,
            // padding: 0,
            rtl: false,
            fixedPoints: null,
            glow: 0,
            glowColor: '#fff',
            clearable: false,
            activeOnClick: false,
            animate: false
        }
    );

    const emit = defineEmits<{
        (e: 'update:rating', value: number): void
        (e: 'hover:rating', value: number): void
    }>();

    const fillLevel = ref<number[]>([]);
    const currentRating = ref<number>(props.rating);
    const selectedRating = ref<number>(props.rating);
    const ratingSelected = ref(false);

    const formattedRating = computed(() => {
        return (props.fixedPoints === null) ? currentRating.value : currentRating.value.toFixed(props.fixedPoints);
    })

    const shouldRound = computed(() => {
        return ratingSelected.value || props.roundStartRating;
    })

    // const margin = computed(() => {
    //     return props.padding + props.borderWidth;
    // })

    const activeColors = computed(() => {
        if (Array.isArray(props.activeColor)) {
            const fillColor = props.activeColor[props.activeColor.length - 1] ?? props.inactiveColor;
            return padColors(props.activeColor, props.maxRating, fillColor);
        }

        return Array.from({ length: props.maxRating }, () => props.activeColor);
    })

    const currentActiveColor = computed(() => {
        if (!props.activeOnClick) {
            return (currentRating.value > 0) ? activeColors.value[Math.ceil(currentRating.value) - 1] : props.inactiveColor;
        }

        return (selectedRating.value > 0) ? activeColors.value[Math.ceil(selectedRating.value) - 1] : props.inactiveColor;
    });

    const activeBorderColors = computed(() => {
        if (Array.isArray(props.activeBorderColor)) {
            const fillColor = props.activeBorderColor[props.activeBorderColor.length - 1] ?? props.borderColor;
            return padColors(props.activeBorderColor, props.maxRating, fillColor);
        }

        let borderColor = (props.activeBorderColor) ? props.activeBorderColor : props.borderColor;

        return Array.from({ length: props.maxRating }, () => borderColor);
    });

    const currentActiveBorderColor = computed(() => {
        if (!props.activeOnClick) {
            return (currentRating.value > 0) ? activeBorderColors.value[Math.ceil(currentRating.value) - 1] : props.borderColor;
        }

        return (selectedRating.value > 0) ? activeBorderColors.value[Math.ceil(selectedRating.value) - 1] : props.borderColor;
    })

    const roundedRating = computed(() => {
        let inv = 1.0 / props.increment;
        return Math.min(props.maxRating, Math.ceil(currentRating.value * inv) / inv);
    })

    const unWatchRating = watch(
        () => props.rating,
        (val) => {
            currentRating.value = val;
            selectedRating.value = val;
            createStars(shouldRound.value);
        }
    );

    function setRating(e: { id: number; position: number }, persist?: boolean) {
        if (!props.readonly) {
            const position = (props.rtl) ? (100 - e.position) / 100 : e.position / 100;
            currentRating.value = Number(((e.id + position) - 1).toFixed(2));
            currentRating.value = (currentRating.value > props.maxRating) ? props.maxRating : currentRating.value;

            if (persist) {
                createStars(true, true);
                selectedRating.value = (props.clearable && currentRating.value === selectedRating.value) ? 0 : currentRating.value;
                emit('update:rating', selectedRating.value);
                ratingSelected.value = true;

            } else {
                createStars(true, !props.activeOnClick);
                emit('hover:rating', currentRating.value);
            }
        }
    }

    function resetRating() {
        if (!props.readonly) {
            currentRating.value = selectedRating.value;
            createStars(shouldRound.value);
        }
    }

    function createStars(round = true, applyFill = true) {
        currentRating.value = (round) ? roundedRating.value : currentRating.value

        for (let i = 0; i < props.maxRating; i++) {
            let level = 0;

            if (i < currentRating.value) {
                level = (currentRating.value - i > 1) ? 100 : (currentRating.value - i) * 100;
            }

            if (applyFill) {
                fillLevel.value[i] = Math.round(level);
            }
        }
    }

    function padColors(array: string[], minLength: number, fillValue: string) {
        return Object.assign(Array.from({ length: minLength }, () => fillValue), array);
    }

    createStars(props.roundStartRating);

    onUnmounted(() => {
        unWatchRating();
    });
</script>

<style lang="scss" scoped>
.vue-star-rating-star {
    display: inline-block;
    -webkit-tap-highlight-color: transparent;
}

.vue-star-rating-pointer {
    cursor: pointer;
}

.vue-star-rating-inline {
    display: inline-flex;
}

.vue-star-rating-rating-text {
    margin-left: 7px;
}

.vue-star-rating-rtl {
    direction: rtl;
}

.vue-star-rating-rtl .vue-star-rating-rating-text {
    margin-right: 10px;
    direction: rtl;
}

.sr-only {
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
}
</style>
